import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import '../styles/EmitirComprobante.css';
import '../../componentes/styles/PreVenta.css';
import Notify from '../../componentes/Notify';
import ClientSelector from '../../componentes/invoice/ClientSelector';
import CustomSelect from '../../componentes/shared/CustomSelect';
import Modal from '../../componentes/Modal';
import { BOLETA, CONTADO, CREDITO, FACTURA, IGV } from '../../Global';
import dr from '../../Global';
import AddItemButton from './componentes/AddItemButton';
import ItemsTable from './componentes/ItemsTable';
import { calculateTotals, esOperacionGravada } from './helpers/calculos';

const formaPagoOptions = [
    { id: CONTADO, nombre: 'CONTADO' },
    { id: CREDITO, nombre: 'CREDITO' }
];

const monedaOptions = [
    { id: 'SOLES', nombre: 'SOLES' },
    { id: 'DOLARES', nombre: 'DOLARES AMERICANOS' }
];

const tipoDocOptions = [
    { id: BOLETA, nombre: 'Boleta Electrónica' },
    { id: FACTURA, nombre: 'Factura Electrónica' }
];

const EmitirComprobante = () => {
    const history = useHistory();
    const [cliente, setCliente] = useState(null);
    const [tipoOperacion, setTipoOperacion] = useState('0101');
    const [tipoOperacionOptions, setTipoOperacionOptions] = useState([]);
    const [unidadOptions, setUnidadOptions] = useState([]);
    const [igvOptions, setIgvOptions] = useState([]);
    const [tipoDoc, setTipoDoc] = useState(BOLETA);
    const [loading, setLoading] = useState(false);
    const [fechaEmision, setFechaEmision] = useState(moment().format('YYYY-MM-DDTHH:mm'));
    const [items, setItems] = useState([]);
    const [formaPago, setFormaPago] = useState(CONTADO);
    const [moneda, setMoneda] = useState('SOLES');
    const [tipoCambio, setTipoCambio] = useState('1.000');
    const [observacion, setObservacion] = useState('');
    const [globalDiscount, setGlobalDiscount] = useState(0);

    const [cantCuotas, setCantCuotas] = useState(1);
    const [cantDias, setCantDias] = useState(30);
    const [cuotas, setCuotas] = useState([]);

    const [availableTipoDocOptions, setAvailableTipoDocOptions] = useState(tipoDocOptions);

    const [idTurnoCaja, setIdTurnoCaja] = useState(null);
    const [puedeVender, setPuedeVender] = useState(true);
    const [modalApertura, setModalApertura] = useState(false);

    useEffect(() => {
        const fetchTipoOperaciones = async () => {
            try {
                const response = await fetch('/api/servicios/tipo-operacion');
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                setTipoOperacionOptions(data);
            } catch (error) {
                console.error("Error al cargar tipos de operación:", error);
            }
        };
        const fetchUnidades = async () => {
            try {
                const response = await fetch('/api/servicios/unidades');
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                setUnidadOptions(data);
            } catch (error) {
                console.error("Error al cargar unidades:", error);
            }
        };
        const fetchAfectacionIgv = async () => {
            try {
                const response = await fetch('/api/servicios/afectacion-igv');
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                setIgvOptions(data);
            } catch (error) {
                console.error("Error al cargar afectación IGV:", error);
            }
        };

        const checkAperturarCaja = async () => {
            let permiso = null;
            try {
                const response = await fetch(`/api/cajas/user/validar`);
                const data = await response.json();
                permiso = data.ValidarUser === true;
            } catch (e) {
                permiso = false;
            }

            if (!permiso) {
                setPuedeVender(true);
                return;
            }

            try {
                const response = await fetch(`/api/cajas/verificar/Turno`);
                const turnoInfo = await response.json();
                
                if (!turnoInfo.Turno || !turnoInfo.Turno[0] || !turnoInfo.Turno[0][0]) {
                    setPuedeVender(false);
                    Notify("Se ha detectado que la caja se encuentra cerrada", "warning");
                    return;
                }

                const turno = turnoInfo.Turno[0][0];
                setIdTurnoCaja(turno.IdTurnoCaja);

                const fechaInicio = moment(turno.FechaInicio);
                const fechaTope = moment(`${fechaInicio.format('YYYY-MM-DD')} 03:30:00`).add({ days: 1 });
                const now = moment();

                const duration = moment.duration(now.diff(fechaTope));
                const shouldReopen = duration.asHours() >= 0;
                
                setModalApertura(shouldReopen);
                setPuedeVender(!shouldReopen);
            } catch (e) {
                setPuedeVender(false);
            }
        };

        fetchTipoOperaciones();
        fetchUnidades();
        fetchAfectacionIgv();
        checkAperturarCaja();
    }, []);

    useEffect(() => {
        if (!cliente) {
            setAvailableTipoDocOptions([]);
            return;
        }

        const docNum = cliente.NroTipoDocumento || cliente.NumeroDocumento || "";
        
        if (docNum.length === 8) {
            setAvailableTipoDocOptions(tipoDocOptions.filter(opt => opt.id === BOLETA));
            setTipoDoc(BOLETA);
        } else if (docNum.length === 11) {
            setAvailableTipoDocOptions(tipoDocOptions);
            setTipoDoc(FACTURA);
        } else {
            setAvailableTipoDocOptions(tipoDocOptions.filter(opt => opt.id === BOLETA));
            setTipoDoc(BOLETA);
        }
    }, [cliente]);

    const handleAddItem = (newItem) => {
        const itemWithId = {
            ...newItem,
            id: Date.now()
        };
        setItems([...items, itemWithId]);
    };

    const removeItem = (id) => {
        setItems(items.filter(item => item.id !== id));
    };

    const handleItemChange = (id, field, value) => {
        setItems(items.map(item => item.id === id ? { ...item, [field]: value } : item));
    };

    const totals = calculateTotals(items, globalDiscount);

    useEffect(() => {
        if (formaPago === CREDITO) {
            const nCuotas = Math.max(1, parseInt(cantCuotas) || 1);
            const nDias = parseInt(cantDias) || 0;
            const totalCentimos = Math.round(parseFloat(totals.total) * 100) || 0;
            
            const nuevasCuotas = [];
            const montoCuotaCentimos = Math.floor(totalCentimos / nCuotas);
            let acumuladoCentimos = 0;

            for (let i = 1; i <= nCuotas; i++) {
                const fecha = moment(fechaEmision).add(nDias * i, 'days').format('YYYY-MM-DD');
                let montoCentimos = montoCuotaCentimos;
                
                if (i === nCuotas) {
                    montoCentimos = totalCentimos - acumuladoCentimos;
                } else {
                    acumuladoCentimos += montoCentimos;
                }

                nuevasCuotas.push({
                    id: i,
                    fecha: fecha,
                    monto: (montoCentimos / 100).toFixed(2)
                });
            }
            setCuotas(nuevasCuotas);
        } else {
            setCuotas([]);
        }
    }, [formaPago, cantCuotas, cantDias, totals.total, fechaEmision]);

    const updateCuota = (id, field, value) => {
        setCuotas(prevCuotas => prevCuotas.map(c => c.id === id ? { ...c, [field]: value } : c));
    };

    const handleEmitir = async () => {
        if (!puedeVender) return Notify('No puede realizar la venta, debe aperturar caja', 'warning');
        if (!cliente) return Notify('Debe seleccionar un cliente', 'error');
        if (items.length === 0) return Notify('Debe agregar al menos un item', 'error');

        if (formaPago === CREDITO) {
            const nCuotasInt = parseInt(cantCuotas) || 0;
            if (nCuotasInt < 1) return Notify('La cantidad de cuotas debe ser mayor o igual a 1', 'error');

            const totalCuotasCentimos = cuotas.reduce((acc, c) => acc + Math.round(parseFloat(c.monto || 0) * 100), 0);
            const totalDocumentoCentimos = Math.round(parseFloat(totals.total) * 100);

            if (totalCuotasCentimos !== totalDocumentoCentimos) {
                return Notify(`El monto total de las cuotas (${(totalCuotasCentimos / 100).toFixed(2)}) no coincide con el total del comprobante (${(totalDocumentoCentimos / 100).toFixed(2)})`, 'error');
            }
        }

        setLoading(true);

        const payload = {
            tipo_operacion: tipoOperacion,
            tipo_documento: tipoDoc,
            fecha_emision: fechaEmision,
            moneda: moneda,
            tipo_cambio: tipoCambio,
            cliente: {
                tipo_documento: cliente.IdTipoDocumento,
                numero: cliente.NroTipoDocumento || cliente.NumeroDocumento,
                razon_social: cliente.RazonSocial || cliente.Nombre,
                direccion: cliente.Direccion
            },
            items: items.map(item => {
                const tasaIgv = esOperacionGravada(String(item.igv)) ? IGV : 0;
                return {
                    unidad: item.unidad,
                    descripcion: item.descripcion,
                    cantidad: String(item.cantidad),
                    unit_price: dr(item.pVenta / (1 + tasaIgv)).toString(),
                    price_with_igv: String(item.pVenta),
                    igv_code: item.igv,
                    descuento: String(item.descuento),
                    has_icbper: item.has_icbper
                };
            }),
            totales: totals,
            forma_pago: formaPago,
            id_turno_caja: idTurnoCaja,
            cuotas: formaPago === CREDITO ? cuotas : [],
            observacion: observacion
        };

        try {
            const response = await fetch('/api/servicios/emitir', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            
            const header = data && data[1] && data[1][0];
            const estado = header ? header._estado : false;

            if (estado === true || estado === 1) {
                Notify(header._mensaje || 'Comprobante emitido correctamente', 'success');
                setTimeout(() => {
                    history.push('/servicios/facturacion');
                }, 500);
            } else {
                Notify(header?._mensaje || 'Error al emitir el comprobante', 'error');
            }
        } catch (error) {
            console.error("Error al emitir comprobante:", error);
            Notify('Hubo un error en la comunicación con el servidor', 'error');
        } finally {
            setLoading(false);
        }
    };

    const isFieldDisabled = !cliente || !puedeVender;

    return (
        <div className="emitir-comprobante-container">
            <div className="emitir-body container-fluid">
                <h3 className='mb-2 text-left font-weight-bold' style={{'color': "#009688"}}>Emisión comprobante Electrónico</h3>
                <div className='row'>
                    <div className='col col-md-8 col-lg-9 mb-2'>
                        <label>Cliente:</label>
                        <ClientSelector onSelectClient={(cli) => setCliente(cli)} />
                    </div>
                    <div className="col col-md-4 col-lg-3 mb-2">
                        <div className="form-group-custom">
                            <label>Fecha emisión</label>
                            <input 
                                type="datetime-local" 
                                className="form-input-custom" 
                                value={fechaEmision}
                                onChange={(e) => setFechaEmision(e.target.value)}
                                disabled={isFieldDisabled}
                            />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6 col-lg-3">
                        <div className="form-group-custom">
                            <label>Tipo de documento</label>
                            <CustomSelect 
                                options={availableTipoDocOptions}
                                label="nombre"
                                value="id"
                                initialValue={tipoDoc}
                                onSelect={(item) => setTipoDoc(item.id)}
                                disabled={isFieldDisabled}
                            />
                        </div>
                    </div>
                    <div className="col-md-6 col-lg-3">
                        <div className="form-group-custom">
                            <label>Tipo de operación</label>
                            <CustomSelect 
                                options={tipoOperacionOptions}
                                label="nombre"
                                value="id"
                                initialValue={tipoOperacion}
                                onSelect={(item) => setTipoOperacion(item.id)}
                                disabled={isFieldDisabled}
                            />
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-4">
                    <h5 className="section-title mb-0 border-0">Detalles de venta</h5>
                    <AddItemButton 
                        onAddItem={handleAddItem} 
                        disabled={isFieldDisabled} 
                        unidadOptions={unidadOptions} 
                        igvOptions={igvOptions}
                    />
                </div>

                <ItemsTable 
                    items={items} 
                    onRemoveItem={removeItem} 
                    onItemChange={handleItemChange} 
                    disabled={isFieldDisabled} 
                    unidadOptions={unidadOptions}
                    igvOptions={igvOptions}
                />

                <div className="summary-container">
                    <div className="summary-table">
                        <div className="summary-row">
                            <span>Descuento global</span>
                            <input 
                                type="number" 
                                className="summary-input"
                                value={globalDiscount}
                                onChange={(e) => setGlobalDiscount(e.target.value)}
                                onFocus={(e) => e.target.select()}
                                disabled={isFieldDisabled}
                            />
                        </div>
                        <div className="summary-row">
                            <span>Gravadas</span>
                            <span>{totals.gravadas}</span>
                        </div>
                        <div className="summary-row">
                            <span>Exoneradas</span>
                            <span>{totals.exoneradas}</span>
                        </div>
                        <div className="summary-row">
                            <span>Inafectas</span>
                            <span>{totals.inafectas}</span>
                        </div>
                        <div className="summary-row">
                            <span>Gratuitas</span>
                            <span>{totals.gratuitas}</span>
                        </div>
                        <div className="summary-row">
                            <span>Exportación</span>
                            <span>{totals.exportacion}</span>
                        </div>
                        <div className="summary-row">
                            <span>ICBPER</span>
                            <span>{totals.icbper}</span>
                        </div>
                        <div className="summary-row">
                            <span>IGV</span>
                            <span>{totals.igv}</span>
                        </div>
                        <div className="summary-row total">
                            <span>Total</span>
                            <span>{moneda === 'SOLES' ? 'S/' : '$'} {totals.total}</span>
                        </div>
                    </div>
                </div>

                <h5 className="section-title">Pago</h5>
                <div className="row">
                    <div className="col-md-4">
                        <div className="form-group-custom">
                            <label>Forma de pago</label>
                            <CustomSelect 
                                options={formaPagoOptions}
                                label="nombre"
                                value="id"
                                initialValue={formaPago}
                                onSelect={(item) => setFormaPago(item.id)}
                                disabled={isFieldDisabled}
                            />
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="form-group-custom">
                            <label>Moneda</label>
                            <CustomSelect 
                                options={monedaOptions}
                                label="nombre"
                                value="id"
                                initialValue={moneda}
                                onSelect={(item) => {
                                    setMoneda(item.id);
                                    if (item.id === 'SOLES') {
                                        setTipoCambio('1.000');
                                    } else if (item.id === 'DOLARES') {
                                        setTipoCambio('3.500');
                                    }
                                }}
                                disabled={isFieldDisabled}
                            />
                        </div>
                    </div>
                    {moneda === 'DOLARES' && (
                        <div className="col-md-4">
                            <div className="form-group-custom">
                                <label>Tipo de cambio</label>
                                <input 
                                    type="number" 
                                    step="0.001"
                                    className="form-input-custom" 
                                    value={tipoCambio}
                                    onChange={(e) => setTipoCambio(e.target.value)}
                                    disabled={isFieldDisabled}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {formaPago === CREDITO && (
                    <>
                        <div className="row mt-2">
                            <div className="col-md-4">
                                <div className="form-group-custom">
                                    <label>Cantidad de cuotas</label>
                                    <input 
                                        type="number" 
                                        className="form-input-custom" 
                                        value={cantCuotas}
                                        onChange={(e) => setCantCuotas(e.target.value)}
                                        min="1"
                                        disabled={isFieldDisabled}
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group-custom">
                                    <label>Días de intervalo</label>
                                    <input 
                                        type="number" 
                                        className="form-input-custom" 
                                        value={cantDias}
                                        onChange={(e) => setCantDias(e.target.value)}
                                        min="0"
                                        disabled={isFieldDisabled}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-3">
                            <h6 className="font-weight-bold">Cronograma de pagos</h6>
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                    <thead className="thead-light">
                                        <tr>
                                            <th style={{width: '10%'}}>#</th>
                                            <th>Fecha de pago</th>
                                            <th>Monto</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cuotas.map((cuota, index) => (
                                            <tr key={cuota.id}>
                                                <td className="align-middle text-center">{index + 1}</td>
                                                <td>
                                                    <input 
                                                        type="date" 
                                                        className="form-control"
                                                        value={cuota.fecha}
                                                        onChange={(e) => updateCuota(cuota.id, 'fecha', e.target.value)}
                                                        disabled={isFieldDisabled}
                                                    />
                                                </td>
                                                <td>
                                                    <div className='input-group'>
                                                        <div className='input-group-prepend'>
                                                            <span className="input-group-text">{moneda === 'SOLES' ? 'S/' : '$'}</span>
                                                        </div>
                                                        <input 
                                                            type="number" 
                                                            className="form-control"
                                                            value={cuota.monto}
                                                            onChange={(e) => updateCuota(cuota.id, 'monto', e.target.value)}
                                                            step="0.01"
                                                            disabled={isFieldDisabled}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-light">
                                            <th colSpan="2" className="text-right">Total cuotas:</th>
                                            <th className="font-weight-bold" style={{"color": "#009688"}}>
                                                {moneda === 'SOLES' ? 'S/' : '$'} {cuotas.reduce((acc, c) => acc + parseFloat(c.monto || 0), 0).toFixed(2)}
                                            </th>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                <h5 className="section-title">Observación</h5>
                <div className="form-group-custom">
                    <textarea 
                        className="form-input-custom" 
                        rows="3"
                        value={observacion}
                        onChange={(e) => setObservacion(e.target.value)}
                        disabled={isFieldDisabled}
                    ></textarea>
                </div>

                {puedeVender && (
                    <div className="emitir-btn-container">
                        <button 
                            className="emitir-btn" 
                            onClick={handleEmitir} 
                            disabled={isFieldDisabled || loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm mr-2 text-light" role="status" aria-hidden="true"></span>
                                    PROCESANDO...
                                </>
                            ) : 'EMITIR COMPROBANTE'}
                        </button>
                    </div>
                )}
                {!puedeVender && (
                    <div className="alert alert-danger text-center mt-4">
                        <strong>Aviso:</strong> No puede emitir comprobantes hasta que aperture una caja.
                    </div>
                )}
            </div>

            <Modal
                isOpen={modalApertura}
                onClose={() => setModalApertura(false)}
                title={"Aviso de apertura de caja"}
            >
                <div className={modalApertura ? "container" : null}
                     style={modalApertura ? {width: "600px"} : null}>

                    <div className="alert alert-warning mt-3" role="alert">
                        <h4 className="alert-heading">Aviso!</h4>
                        <p>
                            Queremos informarle que hemos detectado que la tarea de cierre de caja no ha sido
                            completada.
                        </p>
                    </div>

                    <div className="d-flex justify-content-center my-2">
                        <button
                            className="btn btn-verde-mfp"
                            onClick={() => {
                                Notify("Es necesario cerrar y abrir nuevamente. el turno de la caja y las fechas no coinciden, para garantizar una gestión financiera adecuada. Esto implica hacer un recuento de todo el efectivo, cheques y otros medios de pago, registrar los montos en un informe de cierre de caja y abrir un nuevo turno de caja con la fecha actual. Es recomendable hacerlo con regularidad para mantener una contabilidad precisa.", "warning")
                                history.push(`/AperturarTurnosMovcaja`)
                            }}
                        >Ir a caja
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default EmitirComprobante;
